/* eslint-disable no-magic-numbers */
import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from '@futrli/localisation';
import { PerformanceMetricsCashflowTopItemsByPeriod, PerformanceMetricsCashflowTopCashItem } from '@futrli-sdk/predict';

import { PERIODS } from '../components/period-dropdown';
import { TableWithBarChartCard } from '../components/table-with-bar-chart-card';

interface Props {
  type?: 'in' | 'out';
  currency: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: PerformanceMetricsCashflowTopItemsByPeriod | any;
  testId?: string;
  noDataMessage?: ReactElement;
  cardTitle: ReactElement;
  customHeaders?: ReactElement[];
  customColors?: string | string[];
  customKeys?: string[];
  defaultPeriod?: PERIODS;
  onPeriodChanged?: (p: PERIODS) => void;
}

export const TopCashTable: FC<Props> = ({
  type,
  currency,
  data,
  testId,
  noDataMessage,
  cardTitle,
  customHeaders,
  customColors,
  customKeys,
  defaultPeriod,
  onPeriodChanged
}: Props) => {
  const [periodData, setPeriodData] = useState<PerformanceMetricsCashflowTopCashItem[]>([]);
  const [chartData, setChartData] = useState<{ value: number; label: string }[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const headers = customHeaders
    ? customHeaders
    : [
        <FormattedMessage key="0" id="common.name" defaultMessage="Name" />,
        <FormattedMessage key="1" id="common.amount" defaultMessage="Amount" />,
        <FormattedMessage key="2" id="predict.percent_cash" defaultMessage="% of cash" />
      ];

  useEffect(() => {
    setDataLoading(false);
    if (data) {
      const p = defaultPeriod || PERIODS.THIS_MONTH;
      setPeriodData((data ? data[p] : []) as PerformanceMetricsCashflowTopCashItem[]);
      setChartData(
        (data ? data[p] : []).map((e: { amount: string | number; name: string }) => ({
          value: +e.amount,
          label: e.name
        }))
      );
    }
  }, [data, defaultPeriod]);

  const handleOnChange = useCallback(
    (period: PERIODS) => {
      setPeriodData((data ? data[period] : []) as PerformanceMetricsCashflowTopCashItem[]);
      setChartData(
        (data ? data[period] : []).map((e: { amount: string | number; name: string }) => ({
          value: +e.amount,
          label: e.name
        }))
      );
      onPeriodChanged && onPeriodChanged(period);
    },
    [data, onPeriodChanged]
  );

  return (
    <TableWithBarChartCard
      type={type}
      currency={currency}
      chartData={chartData}
      tableData={periodData}
      dataLoading={dataLoading}
      headers={headers}
      testId={testId || `top-cash-${type}-card`}
      onPeriodChange={handleOnChange}
      noDataMessage={noDataMessage}
      cardTitle={cardTitle}
      customColors={customColors}
      customKeys={customKeys}
      defaultPeriod={defaultPeriod}
    />
  );
};
